import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import dynamic from 'next/dynamic';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { Button } from '@zoopla-eng/zds-components/button';
import { Box } from '@zoopla-eng/zds-components/box';

import { useUser } from '@zoopla/user';
import { getSegmentData } from './utils/getSegmentData';
import { trackGoogleOneTapClose } from './GoogleOneTap.tracking';
import { StepProps, StepsObject } from '../AuthModal/AuthModal.types';
import {
    ResultButtonProps,
    YourSituationStepProps
} from '../AuthModal/Components/YourSituationStep';
import { AuthStepper } from '../AuthModal/Components/Stepper';
import { useAuthSteps } from '../AuthModal/Components/Stepper/useAuthSteps';
import { useAuthModal } from '@zoopla/authentication/use-auth-modal';
import { useGoogleOneTap } from './hooks/useGoogleOneTap';

const LazyPreferencesStep = dynamic<StepProps>(
    () =>
        import('../AuthModal/Components/PreferencesStep').then(
            (mod) => mod.PreferencesStep
        ),
    { ssr: false }
);

const LazyYourSituationStep = dynamic<YourSituationStepProps>(
    () =>
        import('../AuthModal/Components/YourSituationStep').then(
            (mod) => mod.YourSituationStep
        ),
    {
        ssr: false
    }
);

const LazyYourSituationQuestionResultButton = dynamic<ResultButtonProps>(
    () =>
        import('../AuthModal/Components/YourSituationStep').then(
            (mod) => mod.YourSituationResult.Button
        ),
    {
        ssr: false
    }
);

// this is a const otherwise this goes into an infinite re-rendering
const defaultInitialStepsOrder = ['auth', 'onboarding'];
// Stop showing Google One Tap prompt on sign in and register pages
const disallowList = new Set(['/signin', '/register']);

export const GoogleOneTap = () => {
    const [isModalOpen, openAuthModal, closeAuthModal] = useAuthModal();
    const [hasAlreadyClosedModal, setHasAlreadyClosedModal] = useState(false);

    const closeModal = () => {
        setHasAlreadyClosedModal(true);
        closeAuthModal();
    };

    const {
        currentStep,
        yourSituationStepData: { title: _, ...yourSituationStepData },
        enqueueOnboardingAndMoveSteps,
        moveToNextStep
    } = useAuthSteps({
        onDone: closeModal,
        stepsOrder: defaultInitialStepsOrder
    });

    const currentUserSession = useUser();
    const router = useRouter();
    const isGuestUser =
        currentUserSession.loggedIn === false &&
        currentUserSession.loading === false;

    const isInAllowList = !disallowList.has(router.pathname);

    const segmentData = getSegmentData(router.pathname);

    const trackingData = {
        label: 'google_one_tap',
        segmentData
    };

    const authenticationMutation = useGoogleOneTap({
        trackingData
    });

    const authenticate = async () => {
        try {
            const user = await authenticationMutation(undefined);

            enqueueOnboardingAndMoveSteps(user);
            // no need to handle errors here
            // eslint-disable-next-line no-empty
        } catch {}
    };

    const onboardingClose = () => {
        trackGoogleOneTapClose(currentStep);
        closeModal();
    };

    const stepsMap: StepsObject<string> = {
        preferences: {
            title: 'Get expert advice, guidance and inspiration straight to your inbox.',
            trackingCategory: 'signin'
        },
        'your-situation': {
            title: 'Customise your experience',
            trackingCategory:
                yourSituationStepData.questionType === 'default'
                    ? 'registration'
                    : 'signin'
        }
    };

    useEffect(() => {
        if (hasAlreadyClosedModal) {
            return;
        }

        if (currentStep !== 'auth' && !isModalOpen) {
            openAuthModal(null);
        }
    }, [currentStep, hasAlreadyClosedModal, isModalOpen, openAuthModal]);

    if (!isInAllowList) {
        return null;
    }

    // since we log the user in after the auth step we don't want to stop the onboarding from showing
    if (currentStep === 'auth' && !isGuestUser) {
        return null;
    }

    if (currentStep === 'auth' && isGuestUser) {
        return (
            <>
                <style
                    data-testid="google-one-tap-styles"
                    dangerouslySetInnerHTML={{
                        __html: `#credential_picker_container { right: inherit !important; top: inherit !important; left: 1.5rem !important; bottom: 1.5rem !important; }`
                    }}
                />
                <Script
                    src="https://accounts.google.com/gsi/client"
                    onLoad={authenticate}
                    data-testid="google-one-tap-script"
                />
            </>
        );
    }

    return (
        <ControlledDialog
            isOpen={isModalOpen}
            label={stepsMap[currentStep].title}
            title={stepsMap[currentStep].title}
            onClose={onboardingClose}
        >
            <AuthStepper.Provider value={{ moveToNextStep, currentStep }}>
                <AuthStepper.Step id="auth"></AuthStepper.Step>
                <AuthStepper.Step id="preferences">
                    <LazyPreferencesStep
                        onStepCompleted={moveToNextStep}
                        trackingData={trackingData}
                    />
                    <Box pt="S200" display="flex" justifyContent="center">
                        <Button category="tertiary" onClick={moveToNextStep}>
                            Skip and set later in Email preferences
                        </Button>
                    </Box>
                </AuthStepper.Step>
                <AuthStepper.Step id="your-situation">
                    <LazyYourSituationStep
                        trackingData={trackingData}
                        onSkip={moveToNextStep}
                        // this is not called from the YS step, since
                        // the action element takes care of moving to the next step
                        onStepCompleted={() => null}
                        actionElement={
                            <LazyYourSituationQuestionResultButton
                                onClick={moveToNextStep}
                            />
                        }
                        {...yourSituationStepData}
                    />
                </AuthStepper.Step>
            </AuthStepper.Provider>
        </ControlledDialog>
    );
};
